import { routeForBootOutcome } from '@hds/identity-client-sdk'

/**
 * Where a completed boot-auth result sends the app — the one decision between
 * `completeBootAuth` and the router, kept pure so it can be tested without a
 * browser (the portal keeps this as a `switch` inside its main.js).
 *
 * The identity-internal landings are standardized across apps by the client
 * lib's route contract: the choice screens for a login that resolved to no live
 * Human, and the link-outcome acknowledgement a link round trip ends on. The
 * app-exit outcome — where a signed-in person goes — is the target they were
 * headed for.
 *
 * Returns up to one of:
 *   { loginTargetPath }      — signed in; go here once the router is up.
 *   { loginRoundTripFailed } — the provider answered with an error; the guard
 *                              lands on the retry screen instead of bouncing
 *                              back out to the provider forever.
 *   { unlinkedLanding }      — a login with no live Human (choice / restore);
 *                              every unauthenticated navigation lands there.
 *   {}                       — a plain boot; nothing to place.
 */
export function placeBootOutcome(bootAuth) {
    const identityLanding = routeForBootOutcome(bootAuth)
    switch (bootAuth.outcome) {
        case 'authenticated':
            // A parked "connect this login" hand-off ends on the link-outcome
            // page with its verdict; a plain login goes where the person was
            // headed.
            return { loginTargetPath: identityLanding ?? bootAuth.targetPath }
        case 'linked':
        case 'link-failed':
            return { loginTargetPath: identityLanding }
        case 'failed':
            return { loginRoundTripFailed: bootAuth.error }
        default:
            if (identityLanding) {
                return { unlinkedLanding: identityLanding }
            }
            return {}
    }
}
